import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EmptyState, ErrorState, LoadingState, formatDate } from "../components/AdminPageStates";
import { searchUsers, UserDirectoryRow } from "../services/userDirectoryService";

function roleText(user: UserDirectoryRow) {
  const roles = user.roles.length > 0 ? user.roles : [user.role].filter((role): role is string => Boolean(role));
  return roles.length > 0 ? roles.join(", ") : "Unavailable";
}

export default function Users() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<UserDirectoryRow[]>([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  async function loadUsers(nextSearchTerm = searchTerm) {
    setIsLoading(true);
    setError("");

    try {
      console.info("[chor-admin] Users page search submitted", { searchTerm: nextSearchTerm.trim() });
      const nextUsers = await searchUsers(nextSearchTerm);
      console.info("[chor-admin] Users page search rendered", {
        searchTerm: nextSearchTerm.trim(),
        count: nextUsers.length,
        ids: nextUsers.map((user) => user.id)
      });
      setUsers(nextUsers);
    } catch (error) {
      console.error("[chor-admin] Users page search failed", { searchTerm: nextSearchTerm.trim(), error });
      setUsers([]);
      const detail = error instanceof Error && error.message.trim().length > 0 ? ` ${error.message}` : "";
      setError(`Unable to load users. Check Firebase permissions, custom claims, and indexes.${detail}`);
    } finally {
      setIsLoading(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void loadUsers();
  }

  useEffect(() => {
    void loadUsers("");
  }, []);

  return (
    <>
      <form className="search-panel" onSubmit={handleSubmit}>
        <label>
          Search users
          <input
            type="search"
            placeholder="Exact email, username, or UID"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </label>
        <button type="submit" className="primary-button">
          Search
        </button>
      </form>

      {isLoading ? <LoadingState message="Loading users." /> : null}
      {error ? <ErrorState message={error} /> : null}
      {!isLoading && !error && users.length === 0 ? <EmptyState message="Search by exact email, username, or UID." /> : null}

      {!isLoading && !error && users.length > 0 ? (
        <div className="table-shell">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Display name</th>
                <th>Email</th>
                <th>Role(s)</th>
                <th>Created</th>
                <th>Last active</th>
                <th>Guardian/minor</th>
                <th>Profile status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} onClick={() => navigate(`/users/${user.id}`)}>
                  <td>
                    <strong>{user.displayName ?? user.username ?? "Unnamed user"}</strong>
                    <span>{user.id}</span>
                  </td>
                  <td>{user.email ?? "Unavailable"}</td>
                  <td>{roleText(user)}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>{formatDate(user.lastSeenAt)}</td>
                  <td>{user.guardianMinorStatus}</td>
                  <td>{user.profileStatus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
